import React, { useCallback, useState } from 'react';
import { IDocDefaultFields } from '../../../redux/project/documents/sliceProjectDocuments';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../redux/store';
import { Grid2 as Grid } from '@mui/material';
import DocTypeIcon from '../Icons/DocTypeIcon';
import DocStatusIcon from '../Icons/DocStatusIcon';
import DocTableHoverButtons from '../DocTable/DocTableHoverButtons';
import DateTag from '../TextBlocks/DateTag';
import Username from '../TextBlocks/Username';
import MainField from '../TextBlocks/MainField';
import { ProjectSidepanel } from '../../../redux/project/sliceProjectSidepanel';
import { useOpenSidePanel } from '../../../hooks/useOpenSidepanel';
import { getTrimmedString } from '../../../util/UiUtils';
import { getDocTypeOrDefault } from '../../../util/DocUtils';
import { bodyFont } from '../../../Theme';
import { useTranslation } from 'react-i18next';

/**
 * Component for the label content of an AdditionalDocsTreeView item.
 */
export default ({ doc, mainDoc, small, disableClick }: IAdditionalDocsTreeViewLabelProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'additionalDocsTreeViewLabel' });

    const docTypes = useSelector((store: IAppReduxStore) => store.projectDocTypes.data);
    const gridDensity = useSelector((store: IAppReduxStore) => store.userSettings.data.gridDensity);

    const docType = getDocTypeOrDefault(doc, docTypes);
    const hoverButtons = docTypes.find(x => x.name === docType)?.hoverButtons;

    const [showHoverBtns, setShowHoverBtns] = useState<boolean>(false);

    /**
     * Determines the height of the TreeView row according to the DataGrid Density.
     */
    const getRowHeight = useCallback(() => {
        const base = 40;

        if (small) {
            return base;
        }

        switch (gridDensity) {
            case 'standard':
                return base;

            case 'compact':
                return base * 0.7;

            case 'comfortable':
                return base * 1.2;
        }
    }, [gridDensity, small]);

    /**
     * Hook for opening the sidepanel when the user clicks on the main component element.
     */
    const handleClick = useOpenSidePanel({
        UniversalId: doc.UniversalID,
        NoteId: doc.NoteId,
        disableClick,
    });

    /** Additional info on which pages redlinings were made, e.g. "(Page 1,2,3)". */
    const redliningsPages = doc.RedliningPage
        ? ` ${t('page', { pages: doc.RedliningPage.split(',').join(', ') })}`
        : '';

    /** Concatenating Subject and DocVariant to use as the MainField text.  */
    const mainField = getTrimmedString(doc.DocVariant) + ' ' + getTrimmedString(doc.Subject) + redliningsPages;

    /** Check whether to show hover buttons or not. */
    const showHoverButtons = hoverButtons && showHoverBtns;

    return (
        <Grid
            container
            direction={'row'}
            spacing={1}
            onMouseEnter={() => setShowHoverBtns(true)}
            onMouseLeave={() => setShowHoverBtns(false)}
            justifyContent={'space-between'}
            alignItems={'center'}
            sx={{
                height: getRowHeight(),
                width: small ? '100%' : `${document.body.clientWidth * 0.3}px`,
            }}
        >
            <Grid size={'auto'} onClick={e => handleClick(e, ProjectSidepanel.General)}>
                <DocTypeIcon type={docType} color='inherit' tooltipPlacement='top-start' />
            </Grid>
            <Grid size={'auto'}>
                <DocStatusIcon
                    sx={{ cursor: 'default' }}
                    type={doc['ViewIcon_DocumentRelease']}
                    tooltipPlacement='top-start'
                />
            </Grid>
            {small ? (
                <>
                    <Grid size='grow'>
                        <MainField text={mainField} />
                    </Grid>
                    <Grid size={3}>
                        <Username username={doc['Creator']} sx={{ width: 'auto' }} />
                    </Grid>
                    <Grid size={3}>
                        {showHoverButtons ? (
                            <DocTableHoverButtons document={doc} mainDocument={mainDoc} />
                        ) : (
                            <DateTag date={doc['CreationDate']} />
                        )}
                    </Grid>
                </>
            ) : (
                <>
                    <Grid size={'grow'}>
                        <Grid container direction={'row'}>
                            <Grid size={'grow'}>
                                <MainField
                                    text={mainField}
                                    docVariant={doc.DocVariant}
                                    subject={doc.Subject}
                                    showHoverButtons={false}
                                    typographyFontFamily={{ ...bodyFont }}
                                />
                            </Grid>
                            {showHoverButtons && (
                                <Grid size={'auto'}>
                                    <DocTableHoverButtons document={doc} mainDocument={mainDoc} />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Grid size={'auto'}>
                        <Username username={doc['Creator']} sx={{ width: '120px' }} />
                    </Grid>
                    <Grid size={'auto'}>
                        <DateTag date={doc['CreationDate']} />
                    </Grid>
                </>
            )}
        </Grid>
    );
};

/**
 * Interface that describes the props for an AdditionalDocsTreeViewLabel component.
 */
export interface IAdditionalDocsTreeViewLabelProps {
    /** Target document. */
    doc: IDocDefaultFields;

    /** Flag that controls the "small" layout. */
    small: boolean;

    /** (OPTIONAL) Flag that prevents on-click events for DocTypeIcon being triggered. */
    disableClick?: boolean;

    /** Flag that checks whether at least one document has children. */
    hasChildren?: boolean;

    /** Main document. */
    mainDoc?: string;
}
